import { getAuth } from 'firebase/auth'
import { getStorage, ref } from 'firebase/storage'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { fetchArtifactDocxBlobToSave } from '@/artifacts/fetch/get-download-artifact-docx'
import { generateDefaultFilename } from '@/artifacts/util/generate-default-filename'
import uploadFileHandler, { UploadableFile } from '@/routes/dashboard/files/upload-file-handler'
import { kGcsUserFileUploadsBucketRef } from '@/constants/constants-gcs'
import { DocumentExportData, SaveOptions, FolderSelection, FolderSelectionType } from './schemas'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { getArtifactVersion } from '@/artifacts/fetch/get-artifact-version'

export async function buildDocumentObjectForSaving(artifactVersionId: string): Promise<DocumentExportData> {
  const transaction_id = nanoid()

  try {
    const fileObject = await fetchArtifactDocxBlobToSave(artifactVersionId)
    const { name: artifactVersionName } = await getArtifactVersion(artifactVersionId)
    return {
      blob: fileObject.blob,
      defaultFilename: generateDefaultFilename(artifactVersionName),
      artifactVersionId,
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        artifactVersionId,
        transactionId: transaction_id,
      },
    })
    throw error
  }
}

export async function executeSaveArtifactToDrive(exportData: DocumentExportData, options: SaveOptions): Promise<string> {
  const transaction_id = nanoid()
  const { blob, defaultFilename } = exportData
  const { destinationFolder, onProgress } = options

  const auth = getAuth()
  const uid = auth.currentUser?.uid

  if (!uid) {
    throw new Error(kErrorCodeAuthError)
  }

  const folderPath = buildFolderPath(uid, destinationFolder)

  const uploadableFile: UploadableFile = {
    file: blob,
    fileName: defaultFilename,
    id: transaction_id,
  }

  const storage = getStorage(undefined, kGcsUserFileUploadsBucketRef)
  const folderRef = ref(storage, folderPath)

  await new Promise<void>((resolve, reject) => {
    uploadFileHandler({
      uploadableFile,
      currentFolder: folderRef,
      onProgress: (progress) => {
        if (onProgress) {
          onProgress(progress)
        }
      },
      onError: (error) => {
        Sentry.captureException(error, {
          extra: {
            artifactVersionId: exportData.artifactVersionId,
            defaultFilename,
            folderPath,
            transactionId: transaction_id,
          },
        })
        reject(error)
      },
      onComplete: () => {
        resolve()
      },
    })
  })

  return defaultFilename
}

function buildFolderPath(userId: string, selection: FolderSelection): string {
  switch (selection.type) {
    case FolderSelectionType.MATTER:
      return `users/${userId}/${selection.matterId}`
    case FolderSelectionType.CUSTOM:
      return selection.folderRef.fullPath
  }
}
